// TierPromotionCardSemiDumb - accepts a PapiDeviation + CTA button type
// and turns it into primitive attributes of TierPromotionCardDumb
import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import SubscribeButton from '../../Button/SubscribeButton';
import EditTierButton from '../../Tier/EditTierButton';
import { getTierDescription } from '../_helpers';
import SubscribeVariant from '../../Button/SubscribeButton/variants/Subscribe';
import DummySubscribedVariant from '../../Button/SubscribeButton/variants/DummySubscribed';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import HiddenOverlay from '../HiddenOverlay';
import TierPromotionCardEditorDumb, {
  Props as TierPromotionCardDumbProps,
} from './_partials/TierPromotionCardEditorDumb';

import s from './TierPromotionCardEditorDumb.scss';

export type Props = Pick<
  TierPromotionCardDumbProps,
  | 'width'
  | 'height'
  | 'className'
  | 'showEmptyPlaceholders'
  | 'showAuthor'
  | 'style'
  | 'hotspot'
  | 'onClick'
  | 'innerRef'
> & {
  deviation: PapiDeviation;
  ctaButton?:
    | 'subscribe'
    | 'edit'
    | 'view'
    | 'view-subscribe'
    | 'view-subscribed'
    | 'disabled';
  ctaClassname?: string;
  wrapperClassName?: string;
};

/**
 * This is special textRenderer dependency free version of tier card
 */
const TierPromotionCardSemiDumb: React.FC<Props> = ({
  deviation,
  ctaButton,
  ctaClassname,
  wrapperClassName,
  ...props
}) => {
  const [matureFilterOverridden, setMatureFilterOverridden] = useState(false);
  const { showBlocked, overrideMatureFilter } =
    useDeviationMatureFilter(deviation);
  const onOverrideFilterClick = useCallback(() => {
    overrideMatureFilter();
    setMatureFilterOverridden(true);
  }, [overrideMatureFilter]);

  const cover_attachment = useFlag('cover_attachment');

  if (!deviation.tier) {
    return null;
  }
  const { title = '', author, tier } = deviation;

  const { dollarPrice, stats, state } = tier || {};

  let ctaButtonNode;

  switch (ctaButton) {
    case 'subscribe':
      ctaButtonNode = (
        <SubscribeButton
          className={ctaClassname}
          type={'subscribe'}
          tier={deviation}
          matureFilterDisabled
        />
      );
      break;
    case 'view':
      ctaButtonNode = (
        <SubscribeButton
          className={ctaClassname}
          type={'view'}
          tier={deviation}
          matureFilterDisabled
        />
      );
      break;
    case 'view-subscribed':
      ctaButtonNode = (
        <DummySubscribedVariant
          className={ctaClassname}
          onClick={props.onClick}
        />
      );
      break;
    case 'view-subscribe':
      ctaButtonNode = (
        <SubscribeVariant className={ctaClassname} tier={deviation} />
      );
      break;
    case 'edit':
      ctaButtonNode = (
        <EditTierButton className={ctaClassname} tier={deviation} />
      );
      break;
    case 'disabled':
      ctaButtonNode = (
        <SubscribeButton
          className={ctaClassname}
          type={'disabled'}
          tier={deviation}
        />
      );
      break;
    default:
  }

  // if this is being used to show an editor-embedded tier,
  // it may have a frozen version of the deviation which may include the
  // older `coverImage` field we used to store cover images before `media`
  const coverImage =
    deviation.media && cover_attachment ? deviation : deviation.coverImage;
  const rootClasses = classnames(s['card-root'], wrapperClassName);
  return (
    <div className={rootClasses}>
      {showBlocked && (
        <HiddenOverlay
          className={s['card-overlay']}
          compact
          showBackground={false}
          deviation={deviation}
          overrideMatureFilter={onOverrideFilterClick}
        />
      )}
      <TierPromotionCardEditorDumb
        className={s['card']}
        author={author}
        coverImage={coverImage}
        showMatureCover={showBlocked || matureFilterOverridden}
        title={title}
        description={getTierDescription(deviation)}
        stats={stats}
        price={dollarPrice}
        ctaButton={ctaButtonNode}
        state={state}
        {...props}
      />
    </div>
  );
};
TierPromotionCardSemiDumb.displayName = 'TierPromotionCardSemiDumb';

export default TierPromotionCardSemiDumb;
